import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Modal,
  Form,
  DatePicker,
  Avatar,
  Tooltip,
  Select,
  Dropdown,
} from "antd";
import {
  FaBullseye,
  FaSearch,
  FaEdit,
  FaPlus,
  FaCalendarAlt,
  FaChartLine,
  FaLock,
  FaCheckCircle,
  FaPauseCircle,
  FaEllipsisV,
  FaExclamationCircle,
  FaBuilding,
  FaLightbulb,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import dayjs from "dayjs";
import "./Objetivos.css";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return str;
  }
};

const getStatusConfig = (idStatus) => {
  const configs = {
    1: { label: "ABERTO", color: "processing", icon: <FaCheckCircle /> },
    2: { label: "FECHADO", color: "default", icon: <FaLock /> },
    3: { label: "SUSPENSO", color: "warning", icon: <FaPauseCircle /> },
    4: { label: "EM ANÁLISE", color: "purple", icon: <FaExclamationCircle /> },
  };
  return configs[idStatus] || configs[1];
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE ADICIONAR/EDITAR OBJETIVO
───────────────────────────────────────────────────────────────────────────── */
const ObjetivoFormModal = ({
  open,
  onClose,
  onSuccess,
  editingObjetivo,
  statusList,
  direcaoList,
  iniciativaList,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const isStatusSuspenso = editingObjetivo?.id_status === 3;
  const isStatusFechado = editingObjetivo?.id_status === 2;

  useEffect(() => {
    if (open) {
      if (editingObjetivo) {
        form.setFieldsValue({
          titulo: editingObjetivo.titulo,
          descricao: editingObjetivo.descricao,
          inicio: editingObjetivo.inicio ? dayjs(editingObjetivo.inicio) : null,
          fim: editingObjetivo.fim ? dayjs(editingObjetivo.fim) : null,
          id_status: editingObjetivo.id_status,
          id_direcao: editingObjetivo.id_direcao ?? undefined,
          id_iniciativa: editingObjetivo.id_iniciativa ?? undefined,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ id_status: 1 });
      }
    }
  }, [open, editingObjetivo, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        titulo: values.titulo,
        descricao: values.descricao || null,
        inicio: values.inicio ? values.inicio.format("YYYY-MM-DD") : null,
        fim: values.fim ? values.fim.format("YYYY-MM-DD") : null,
        id_status: values.id_status,
        id_direcao: values.id_direcao ?? null,
        id_iniciativa: values.id_iniciativa ?? null,
      };

      if (editingObjetivo) {
        await Api.AlterObjetivo({ ...payload, id: editingObjetivo.id });
        toast.success("Objetivo atualizado com sucesso!");
      } else {
        await Api.AddObjetivo(payload);
        toast.success("Objetivo criado com sucesso!");
      }

      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        toast.error("Preencha todos os campos obrigatórios.");
      } else {
        toast.error(
          editingObjetivo
            ? "Erro ao atualizar objetivo."
            : "Erro ao criar objetivo."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const getAvailableStatusOptions = () => {
    if (!editingObjetivo) return statusList.filter((s) => s.id === 1);
    const currentStatus = editingObjetivo.id_status;
    if (currentStatus === 1) return statusList.filter((s) => [1, 2, 3].includes(s.id));
    if (currentStatus === 3) return statusList.filter((s) => [1, 3].includes(s.id));
    if (currentStatus === 2) return statusList.filter((s) => s.id === 2);
    if (currentStatus === 4) return statusList;
    return statusList;
  };

  const isDisabled = isStatusFechado || isStatusSuspenso;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={620}
      title={
        <Space>
          <FaBullseye style={{ color: "#1677ff", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>
            {editingObjetivo ? "Editar Objetivo" : "Novo Objetivo"}
          </span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 6 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={isStatusFechado}
          style={{ borderRadius: 6, fontWeight: 500 }}
        >
          {editingObjetivo ? "Atualizar" : "Criar Objetivo"}
        </Button>,
      ]}
      bodyStyle={{ padding: "24px 24px 0" }}
    >
      {/* Alertas de status */}
      {isStatusFechado && (
        <div style={alertStyle("#fff7e6", "#ffd591")}>
          <FaLock style={{ color: "#fa8c16" }} />
          <Text style={{ color: "#ad6800" }}>
            Objetivos fechados não podem ser editados.
          </Text>
        </div>
      )}
      {isStatusSuspenso && (
        <div style={alertStyle("#fffbe6", "#ffe58f")}>
          <FaPauseCircle style={{ color: "#faad14" }} />
          <Text style={{ color: "#ad8b00" }}>
            Para editar este objetivo, altere o status para ABERTO.
          </Text>
        </div>
      )}

      <Form form={form} layout="vertical">
        {/* Status — só visível na edição */}
        {editingObjetivo && (
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Status</span>}
            name="id_status"
            rules={[{ required: true, message: "Campo obrigatório" }]}
          >
            <Select size="large" disabled={isStatusFechado} placeholder="Selecione o status">
              {getAvailableStatusOptions().map((status) => {
                const config = getStatusConfig(status.id);
                return (
                  <Select.Option key={status.id} value={status.id}>
                    <Space>
                      {config.icon}
                      {status.descricao}
                    </Space>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Título</span>}
          name="titulo"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <Input
            placeholder="Ex: Aumentar receita em 20%"
            size="large"
            disabled={isDisabled}
          />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Descrição</span>}
          name="descricao"
        >
          <TextArea
            rows={3}
            placeholder="Detalhes do objetivo (opcional)"
            disabled={isDisabled}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data de Início</span>}
              name="inicio"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                size="large"
                disabled={isDisabled}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data de Fim</span>}
              name="fim"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                size="large"
                disabled={isDisabled}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <Space size={4}>
                  <FaBuilding style={{ color: "#8c8c8c" }} />
                  <span style={{ fontWeight: 500 }}>Direção</span>
                </Space>
              }
              name="id_direcao"
            >
              <Select
                size="large"
                allowClear
                placeholder="Selecione a direção (opcional)"
                disabled={isDisabled}
                showSearch
                optionFilterProp="children"
              >
                {direcaoList.map((d) => (
                  <Select.Option key={d.id} value={d.id}>
                    {d.descricao}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <Space size={4}>
                  <FaLightbulb style={{ color: "#8c8c8c" }} />
                  <span style={{ fontWeight: 500 }}>Iniciativa</span>
                </Space>
              }
              name="id_iniciativa"
            >
              <Select
                size="large"
                allowClear
                placeholder="Selecione a iniciativa (opcional)"
                disabled={isDisabled}
                showSearch
                optionFilterProp="children"
              >
                {iniciativaList.map((i) => (
                  <Select.Option key={i.id} value={i.id}>
                    {i.titulo}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

const alertStyle = (bg, border) => ({
  backgroundColor: bg,
  border: `1px solid ${border}`,
  borderRadius: 8,
  padding: 12,
  marginBottom: 16,
  display: "flex",
  alignItems: "center",
  gap: 8,
});

/* ─────────────────────────────────────────────────────────────────────────────
   PÁGINA PRINCIPAL DE OBJETIVOS
───────────────────────────────────────────────────────────────────────────── */
const Objetivos = () => {
  const [objetivos, setObjetivos] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [direcaoList, setDirecaoList] = useState([]);
  const [iniciativaList, setIniciativaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingObjetivo, setEditingObjetivo] = useState(null);

  useEffect(() => {
    document.title = "Gestão de Objetivos | Dashboard";
  }, []);

  const loadStatusList = useCallback(async () => {
    try {
      const response = await Api.ListStatus();
      setStatusList(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar lista de status.");
    }
  }, []);

  const loadDirecaoList = useCallback(async () => {
    try {
      const response = await Api.ListDirecao();
      setDirecaoList(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar lista de direções.");
    }
  }, []);

  const loadIniciativaList = useCallback(async () => {
    try {
      const response = await Api.ListIniciativa();
      setIniciativaList(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar lista de iniciativas.");
    }
  }, []);

  const loadObjetivos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListObjetivo();
      setObjetivos(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar objetivos.");
      setObjetivos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatusList();
    loadDirecaoList();
    loadIniciativaList();
    loadObjetivos();
  }, [loadStatusList, loadDirecaoList, loadIniciativaList, loadObjetivos]);

  const openFormModal = (objetivo = null) => {
    setEditingObjetivo(objetivo);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingObjetivo(null);
  };

  const handleQuickStatusChange = async (objetivo, newStatusId) => {
    try {
      await Api.AlterObjetivo({
        id: objetivo.id,
        titulo: objetivo.titulo,
        descricao: objetivo.descricao,
        inicio: objetivo.inicio,
        fim: objetivo.fim,
        id_status: newStatusId,
        id_direcao: objetivo.id_direcao ?? null,
        id_iniciativa: objetivo.id_iniciativa ?? null,
      });
      toast.success("Status alterado com sucesso!");
      loadObjetivos();
    } catch {
      toast.error("Erro ao alterar status.");
    }
  };

  const getQuickActionMenu = (objetivo) => {
    const items = [];
    const currentStatus = objetivo.id_status;

    if (currentStatus === 1) {
      items.push({
        key: "fechar",
        label: (
          <Space>
            <FaLock style={{ color: "#8c8c8c" }} />
            Fechar Objetivo
          </Space>
        ),
        onClick: () => handleQuickStatusChange(objetivo, 2),
      });
      items.push({
        key: "suspender",
        label: (
          <Space>
            <FaPauseCircle style={{ color: "#faad14" }} />
            Suspender Objetivo
          </Space>
        ),
        onClick: () => handleQuickStatusChange(objetivo, 3),
      });
    }

    if (currentStatus === 3) {
      items.push({
        key: "abrir",
        label: (
          <Space>
            <FaCheckCircle style={{ color: "#52c41a" }} />
            Reabrir Objetivo
          </Space>
        ),
        onClick: () => handleQuickStatusChange(objetivo, 1),
      });
    }

    if (currentStatus !== 2) {
      items.push({
        key: "editar",
        label: (
          <Space>
            <FaEdit style={{ color: "#1677ff" }} />
            Editar Detalhes
          </Space>
        ),
        onClick: () => openFormModal(objetivo),
      });
    }

    return items;
  };

  // Estatísticas
  const totalObjetivos = objetivos.length;
  const objetivosAbertos = objetivos.filter((obj) => obj.id_status === 1).length;
  const objetivosFechados = objetivos.filter((obj) => obj.id_status === 2).length;
  const objetivosSuspensos = objetivos.filter((obj) => obj.id_status === 3).length;

  // Filtragem
  const filtered = objetivos.filter((obj) => {
    const q = search.toLowerCase();
    return (
      !q ||
      obj.titulo?.toLowerCase().includes(q) ||
      obj.descricao?.toLowerCase().includes(q)
    );
  });

  // Helpers para resolver nomes a partir dos IDs
  const getDirecaoNome = (id) =>
    direcaoList.find((d) => d.id === id)?.descricao ?? null;

  const getIniciativaTitulo = (id) =>
    iniciativaList.find((i) => i.id === id)?.titulo ?? null;

  const columns = [
    {
      title: "Objetivo",
      dataIndex: "titulo",
      key: "titulo",
      width: 280,
      render: (titulo, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            shape="square"
            size="large"
            style={{
              backgroundColor: "#f0f5ff",
              color: "#1677ff",
              borderRadius: 8,
              flexShrink: 0,
            }}
            icon={<FaBullseye />}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 2 }}>
              {titulo || <Text type="secondary">Sem título</Text>}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.descricao?.length > 50
                ? record.descricao.substring(0, 50) + "..."
                : record.descricao || "—"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Período",
      key: "periodo",
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.inicio)}
          </Text>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.fim)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Direção / Iniciativa",
      key: "vinculo",
      width: 220,
      render: (_, record) => {
        const direcao = record.id_direcao ? getDirecaoNome(record.id_direcao) : null;
        const iniciativa = record.id_iniciativa
          ? getIniciativaTitulo(record.id_iniciativa)
          : null;

        if (!direcao && !iniciativa) {
          return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        }

        return (
          <Space direction="vertical" size={4}>
            {direcao && (
              <Tooltip title={direcao}>
                <Tag
                  icon={<FaBuilding style={{ marginRight: 4 }} />}
                  color="blue"
                  style={{ maxWidth: 190, overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {direcao.length > 28 ? direcao.substring(0, 28) + "…" : direcao}
                </Tag>
              </Tooltip>
            )}
            {iniciativa && (
              <Tooltip title={iniciativa}>
                <Tag
                  icon={<FaLightbulb style={{ marginRight: 4 }} />}
                  color="gold"
                  style={{ maxWidth: 190, overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {iniciativa.length > 28 ? iniciativa.substring(0, 28) + "…" : iniciativa}
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 140,
      render: (_, record) => {
        const config = getStatusConfig(record.id_status);
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: "Ações",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => {
        const menuItems = getQuickActionMenu(record);

        if (menuItems.length === 0) {
          return (
            <Tooltip title="Objetivo fechado — sem ações disponíveis">
              <Button type="text" icon={<FaLock />} disabled />
            </Tooltip>
          );
        }

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button
              type="text"
              icon={<FaEllipsisV />}
              style={{ color: "#1677ff" }}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Layout className="objetivos-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaBullseye style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title
                    level={2}
                    style={{ color: "#fff", margin: 0, fontWeight: 700 }}
                  >
                    Gestão de Objetivos
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}>
                    Defina metas claras e acompanhe a evolução
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                size="large"
                icon={<FaPlus />}
                onClick={() => openFormModal()}
                style={{
                  backgroundColor: "#fff",
                  color: "#1677ff",
                  border: "none",
                  fontWeight: 600,
                  borderRadius: 30,
                  padding: "0 24px",
                  height: 48,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                Novo Objetivo
              </Button>
            </Col>
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          {[
            {
              title: "Total de Objetivos",
              value: totalObjetivos,
              color: "#1677ff",
              bg: "#f0f5ff",
              icon: <FaBullseye style={{ fontSize: 24, color: "#1677ff" }} />,
            },
            {
              title: "Objetivos Abertos",
              value: objetivosAbertos,
              color: "#52c41a",
              bg: "#f6ffed",
              icon: <FaCheckCircle style={{ fontSize: 24, color: "#52c41a" }} />,
            },
            {
              title: "Objetivos Fechados",
              value: objetivosFechados,
              color: "#8c8c8c",
              bg: "#fafafa",
              icon: <FaLock style={{ fontSize: 24, color: "#8c8c8c" }} />,
            },
            {
              title: "Suspensos",
              value: objetivosSuspensos,
              color: "#faad14",
              bg: "#fffbe6",
              icon: <FaPauseCircle style={{ fontSize: 24, color: "#faad14" }} />,
            },
          ].map((stat) => (
            <Col xs={24} sm={12} lg={6} key={stat.title}>
              <Card
                className="stat-card"
                bordered={false}
                style={{
                  borderRadius: 16,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: "20px 24px" }}
              >
                <Statistic
                  title={
                    <span style={{ fontSize: 14, color: "#8c8c8c" }}>
                      {stat.title}
                    </span>
                  }
                  value={stat.value}
                  valueStyle={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: stat.color,
                  }}
                  prefix={
                    <div
                      style={{
                        backgroundColor: stat.bg,
                        borderRadius: 12,
                        padding: 10,
                        marginRight: 16,
                      }}
                    >
                      {stat.icon}
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barra de pesquisa */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Input
            size="large"
            prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
            placeholder="Pesquisar por título ou descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ borderRadius: 30 }}
          />
        </Card>

        {/* Tabela */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} objetivos`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhum objetivo encontrado" }}
            scroll={{ x: 1000 }}
          />
        </Card>

        {/* Modal */}
        <ObjetivoFormModal
          open={formModalOpen}
          onClose={closeFormModal}
          onSuccess={loadObjetivos}
          editingObjetivo={editingObjetivo}
          statusList={statusList}
          direcaoList={direcaoList}
          iniciativaList={iniciativaList}
        />
      </Content>
    </Layout>
  );
};

export default Objetivos;